import * as t from 'io-ts';

import { Currency, Decoding, Money } from '@catawiki/runtime-toolkit';
import { I18nInstance } from '@catawiki/cata-style';

export const LotPrice = t.type({
  auction_id: t.number,
  bidding_end_time: t.string,
  bidding_start_time: t.string,
  closed: t.boolean,
  current_bid_amount: t.type({
    EUR: t.number,
    GBP: t.number,
    USD: t.number,
  }),
  favorite_count: t.number,
  highest_bidder_token: Decoding.nullable(t.string),
  id: t.number,
  realtime_channel: t.string,
  reserve_price_met: Decoding.nullable(t.boolean),
});
export type LotPrice = t.TypeOf<typeof LotPrice>;

export const ILot = t.type({
  id: t.number,
  imageUrl: t.string,
  lotUrl: t.string,
  title: t.string,
});

export type Lot = t.TypeOf<typeof ILot>;

export const IEnrichedLot = t.type({
  id: t.number,
  imageUrl: t.string,
  lotUrl: t.string,
  title: t.string,
  price: t.string,
  closed: t.boolean,
  favoriteCount: t.number,
  lotPriceInCurrencies: t.union([LotPrice, t.undefined]),
});

export type EnrichedLot = t.TypeOf<typeof IEnrichedLot>;

function findLotInLotPrices(
  lot: Lot,
  lotPrices: LotPrice[]
): LotPrice | undefined {
  if (!lot) return undefined;
  return lotPrices.find((lotPrice) => lotPrice.id === lot.id);
}

export function enrichLotWithPriceData(
  i18n: I18nInstance,
  lot: Lot,
  lotPrices: LotPrice[]
): EnrichedLot {
  const lotPrice = findLotInLotPrices(lot, lotPrices);
  const enrichedLotPrice = {
    closed: lotPrice ? lotPrice.closed : false,
    favoriteCount: lotPrice?.favorite_count ?? 0,
    lotPriceInCurrencies: lotPrice,
    price: lotPrice
      ? Money.toCurrency(
          i18n,
          lotPrice.current_bid_amount.EUR,
          Currency.Currency.EURO,
          { precision: 0 }
        )
      : Money.toCurrency(i18n, 1, Currency.Currency.EURO, { precision: 0 }),
  };
  return {
    ...lot,
    ...enrichedLotPrice,
  };
}

export function enrichLotsWithPriceData(
  i18n: I18nInstance,
  lots: Lot[],
  lotPrices: LotPrice[]
): EnrichedLot[] {
  return lots.map((lot) => enrichLotWithPriceData(i18n, lot, lotPrices));
}
